// Sincroniza las preferencias del chart (layout/paleta/fondo, indicadores
// activos, sus configuraciones, favoritos) con la cuenta del usuario en el
// backend, en vez de dejarlas solo en localStorage del navegador — así un
// usuario ve su propia configuración sin importar desde qué dispositivo
// entra, y dos cuentas distintas en el mismo navegador no se pisan la una a
// la otra. Ver GET/PATCH /auth/chart-preferences (app privada) y
// GET/PATCH /api/public/chart-preferences (chart público).
//
// Diseño deliberadamente mínimo y "aditivo": cada uno de los 4 hooks
// (useChartLayout, useIndicatorState, useIndicatorConfig,
// useStructureConfig) y el estado de favoritos de cada página siguen
// leyendo/escribiendo localStorage exactamente igual que antes (primer
// pintado instantáneo, funciona sin red) — este módulo sólo añade, por
// encima, una carga remota inicial (que sobreescribe el estado local si el
// backend ya tenía algo guardado) y un guardado remoto con batching global.
// El batching agrupa todas las claves modificadas en una ventana de 1s y
// envía hasta 10 PATCHes por flush, evitando la tormenta de escrituras que
// agotaba el rate limiter del backend cuando el usuario hacía cambios rápidos
// (activar/desactivar indicadores, mover sliders, etc.). Si ninguna página
// llama a initRemotePreferences/setRemotePatchFn (p.ej. en tests o en un
// contexto sin cuenta), esto se queda inerte y el comportamiento es idéntico
// al de antes de este módulo.

let cache = null // dict completo de preferencias ya cargado del backend, o null si aún no
let loadFailed = false // true si el GET inicial de preferencias falló (ver initRemotePreferences)
let patchFn = null // (key, value) => Promise<void>, inyectado por la página (axios vs fetch)

// Batching global de escrituras: todas las claves se agrupan y se envían en
// una sola ráfaga controlada. Evita la tormenta de PATCHes que agotaba el rate
// limiter del backend cuando el usuario hacía cambios rápidos (activar/desactivar
// indicadores, mover sliders, etc.) — cada cambio generaba un timer de 700ms
// por clave y las ráfagas de 10+ claves distintas en 100ms superaban el límite.
const pendingWrites = new Map() // key → value (último valor pendiente por clave)
let globalWriteTimer = null
const GLOBAL_WRITE_DELAY_MS = 1000 // ventana de agrupación
const MAX_PATCHES_PER_FLUSH = 10 // máximo de PATCHes por flush (dentro del burst del edge)

// Un solo flush con PATCHes en vuelo a la vez. Bug P0 (2026-08-12): antes,
// `globalWriteTimer` se ponía a null al ARRANCAR el flush (antes de que los
// PATCH resolvieran), así que un saveRemote() que llegaba mientras el
// primero seguía en vuelo (red lenta / BD fría — típico en la primera
// escritura de una cuenta recién creada) veía el timer libre y agendaba un
// SEGUNDO flush independiente. Si ese segundo flush disparaba antes de que
// el primero hubiera resuelto, dos PATCHes de claves distintas quedaban en
// vuelo simultáneamente. El backend (SELECT → merge en Python → commit, sin
// lock) pierde la clave del que comitea primero: lost-update clásico — el
// usuario activaba su primer indicador y no se le guardaba. Fix: un flag
// `isFlushing` serializa los flushes; mientras uno está en vuelo, saveRemote
// no agenda uno nuevo — las claves que lleguen se recogen cuando el flush en
// curso termina y su propio `.then()` reagenda si queda trabajo pendiente.
let isFlushing = false

function flushPendingWrites() {
  if (!patchFn || loadFailed || pendingWrites.size === 0 || isFlushing) return
  isFlushing = true
  const entries = Array.from(pendingWrites.entries()).slice(0, MAX_PATCHES_PER_FLUSH)
  for (const [key] of entries) pendingWrites.delete(key)
  clearTimeout(globalWriteTimer)
  globalWriteTimer = null

  entries.reduce((promise, entry) => {
    const [k, v] = entry
    return promise.then(() => patchFn(k, v).then(() => {
      clearUnsynced(k)
    }).catch(() => {
      /* red caída: localStorage sigue teniendo el valor, y el ledger sigue
         marcando la clave como no confirmada para la próxima carga */
    }))
  }, Promise.resolve()).then(() => {
    isFlushing = false
    if (pendingWrites.size > 0) {
      globalWriteTimer = setTimeout(flushPendingWrites, GLOBAL_WRITE_DELAY_MS)
    }
  })
}

// Ledger de "claves con un guardado remoto todavía no confirmado", persistido
// en localStorage (sobrevive a un F5). Bug P0 (2026-08-12, "F5 inmediato
// pierde el estado"): un F5 real CANCELA cualquier PATCH todavía en vuelo —
// eso ninguna solución de solo-JS puede evitarlo. Lo que sí se puede evitar:
// que la hidratación remota de la página recién cargada aplique un snapshot
// del servidor que es MÁS VIEJO que lo que ese PATCH cancelado iba a
// escribir. Un hook consulta `hasUnsyncedLocalWrite(key)` antes de aplicar
// `migrate(remote)` — si hay un guardado pendiente de confirmar, el estado
// local (ya cargado de localStorage, más nuevo) gana y NO se pisa.
//
// TTL, no "para siempre" (bug encontrado en la propia validación E2E de este
// fix, cazado por snapshot_semantics.spec.ts): si el flag se quedara
// marcado indefinidamente hasta que ALGUNA carga futura consiga completar el
// flush, un dispositivo que cierra la pestaña antes de que el guardado
// asiente dejaría el ledger "sucio" para siempre — la SIGUIENTE carga (y
// todas las de después, aunque pasen días) ignorarían cualquier cambio
// legítimo hecho desde OTRO dispositivo mientras tanto, re-empujando el
// snapshot local obsoleto por encima. Con TTL, pasado ese margen se asume
// que el guardado real ya llegó (o que ya no importa preservarlo a ciegas) y
// la hidratación vuelve a confiar en el servidor con normalidad.
const UNSYNCED_LEDGER_KEY = '__remote_prefs_unsynced__'
const UNSYNCED_TTL_MS = 2 * 60 * 1000 // 2 minutos

function readUnsyncedLedger() {
  if (typeof window === 'undefined') return {}
  try {
    return JSON.parse(localStorage.getItem(UNSYNCED_LEDGER_KEY)) || {}
  } catch {
    return {}
  }
}

function markUnsynced(key) {
  if (typeof window === 'undefined') return
  try {
    const ledger = readUnsyncedLedger()
    ledger[key] = Date.now()
    localStorage.setItem(UNSYNCED_LEDGER_KEY, JSON.stringify(ledger))
  } catch {}
}

function clearUnsynced(key) {
  if (typeof window === 'undefined') return
  try {
    const ledger = readUnsyncedLedger()
    if (key in ledger) {
      delete ledger[key]
      localStorage.setItem(UNSYNCED_LEDGER_KEY, JSON.stringify(ledger))
    }
  } catch {}
}

// Consulta síncrona: ¿esta clave tiene un guardado local que todavía no se
// sabe si llegó al servidor? (encolado, en vuelo, o cuya última confirmación
// se perdió por un F5/cierre de pestaña a mitad de la petición) — sólo
// dentro de la ventana TTL; pasado ese margen se considera expirado.
export function hasUnsyncedLocalWrite(key) {
  if (pendingWrites.has(key)) return true
  const markedAt = readUnsyncedLedger()[key]
  return typeof markedAt === 'number' && (Date.now() - markedAt) < UNSYNCED_TTL_MS
}

let resolveLoaded
let loadedGate = new Promise((resolve) => { resolveLoaded = resolve })

export function initRemotePreferences(getFn) {
  cache = null
  loadFailed = false
  pendingWrites.clear()
  clearTimeout(globalWriteTimer)
  globalWriteTimer = null
  loadedGate = new Promise((resolve) => { resolveLoaded = resolve })
  return getFn()
    .then((prefs) => { cache = prefs || {} })
    .catch(() => { cache = {}; loadFailed = true })
    .finally(() => resolveLoaded())
}

export function setRemotePatchFn(fn) {
  patchFn = fn
}

export async function getRemoteValue(key) {
  await loadedGate
  return cache ? cache[key] : undefined
}

export function saveRemote(key, value) {
  if (!patchFn || loadFailed) return
  markUnsynced(key)
  pendingWrites.set(key, value)
  // Si ya hay un flush en vuelo, no agendar uno nuevo: su propio `.then()`
  // reagendará al terminar si queda trabajo (ver flushPendingWrites). Evita
  // un timer redundante que dispara en no-op mientras isFlushing es true.
  if (globalWriteTimer == null && !isFlushing) {
    globalWriteTimer = setTimeout(flushPendingWrites, GLOBAL_WRITE_DELAY_MS)
  }
}
